import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  CheckboxInput,
  FormContainer,
  NextButton,
  Option,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { requiredArrayOfStrings } from "~/utils/validations";

type Values = {
  providersSeen: string[];
  providersOtherDetails: string;
};

export const reiClinic =
  "Reproductive endocrinologist (a.k.a. a fertility specialist)";

const validationSchema = Yup.object({
  providersSeen: requiredArrayOfStrings,
  providersOtherDetails: Yup.string().test(
    "providersOtherDetails",
    'This field is required when "Other" is selected.',
    function (value) {
      const { providersSeen } = this.parent;
      return providersSeen && providersSeen.includes("Other")
        ? Boolean(value)
        : true;
    },
  ),
});

const options: Option[] = [
  { text: "OB-GYN", value: "OB-GYN" },
  { text: "Urologist", value: "Urologist" },
  {
    text: "Reproductive endocrinologist (a.k.a. a fertility specialist)",
    value: reiClinic,
  },
  { text: "Therapist", value: "Therapist" },
  { text: "Acupuncturist or herbalist", value: "Acupuncturist or herbalist" },
  { text: "Genetic counselor", value: "Genetic counselor" },
  { text: "Midwife", value: "Midwife" },
  { text: "Doula", value: "Doula" },
  { text: "Lactation consultant", value: "Lactation consultant" },
  { text: "Other", value: "Other" },
  { text: "None", value: "None" },
];

export const ProvidersSeenPage: FC = () => {
  const { t } = useTranslation();
  const { providersOtherDetails, providersSeen } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();

  const initialValues: Values = {
    providersSeen: providersSeen || [],
    providersOtherDetails: providersOtherDetails || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const showAdditionalQuestion =
    formik?.values?.providersSeen?.includes("Other");

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            description={t("onboarding.providersSeen.description")}
            question={t("onboarding.providersSeen.question")}
          />

          <CheckboxInput
            defaultValue={initialValues.providersSeen}
            id="providersSeen"
            options={options}
          />
        </QuestionContainer>

        {showAdditionalQuestion && (
          <QuestionContainer my={5}>
            <Prompt
              question={t(
                "onboarding.providersSeen.additionalQuestion.question",
              )}
            />

            <StringInput
              id="providersOtherDetails"
              placeholder={t(
                "onboarding.providersSeen.additionalQuestion.placeholder",
              )}
            />
          </QuestionContainer>
        )}

        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
